import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

/**
 * TeamWidget
 *
 * Highlights the most active team members, roles, and a task-completion summary.
 */

const roleColor = (role) => {
  const r = role.toLowerCase();
  if (r.includes("manager") || r.includes("director") || r.includes("lead")) return "bg-emerald-100 text-emerald-800";
  if (r.includes("engineer") || r.includes("specialist")) return "bg-blue-100 text-blue-800";
  if (r.includes("agronomist") || r.includes("scientist") || r.includes("researcher")) return "bg-purple-100 text-purple-800";
  if (r.includes("veterinarian")) return "bg-pink-100 text-pink-800";
  return "bg-slate-100 text-slate-700";
};

const getInitials = (name) => {
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0]?.[0]?.toUpperCase() || "?";
};

const avatarColors = [
  "bg-emerald-600", "bg-blue-600", "bg-purple-600", "bg-amber-600",
  "bg-rose-600", "bg-teal-600", "bg-indigo-600", "bg-orange-600",
];

const TeamWidget = ({ team, isArabic }) => {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-30px" });

  if (!team) return null;

  const { totalMembers, members = [], tasksSummary } = team;
  const completionPct = tasksSummary ? Math.round((tasksSummary.completed / tasksSummary.total) * 100) : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden h-full">
      {/* Header */}
      <div className={`px-5 py-4 border-b border-slate-100 flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center text-lg flex-shrink-0">👥</div>
        <div className={isArabic ? "text-right" : ""}>
          <h3 className="text-sm font-bold text-slate-900 mb-0">
            {isArabic ? "الفريق والمهام" : "Team & Tasks"}
          </h3>
          <p className="text-[11px] text-slate-400 mb-0">
            {totalMembers} {isArabic ? "أعضاء" : "members"}
          </p>
        </div>
      </div>

      <div ref={ref} className="p-5 space-y-4">
        {/* Task completion summary bar */}
        {tasksSummary && (
          <div>
            <div className={`flex items-center justify-between mb-2 ${isArabic ? "flex-row-reverse" : ""}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0">
                {isArabic ? "إنجاز المهام" : "Task Completion"}
              </p>
              <span className="text-xs font-bold text-emerald-600">{completionPct}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"
                initial={{ width: 0 }}
                animate={inView ? { width: `${completionPct}%` } : { width: 0 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
              />
            </div>
            <div className={`flex items-center gap-4 mt-2 ${isArabic ? "flex-row-reverse" : ""}`}>
              <span className="text-[10px] text-slate-500">
                <span className="font-bold text-emerald-600">{tasksSummary.completed}</span> {isArabic ? "مكتمل" : "done"}
              </span>
              <span className="text-[10px] text-slate-500">
                <span className="font-bold text-blue-600">{tasksSummary.inProgress}</span> {isArabic ? "قيد التنفيذ" : "in progress"}
              </span>
              <span className="text-[10px] text-slate-500">
                <span className="font-bold text-slate-500">{tasksSummary.pending}</span> {isArabic ? "معلق" : "pending"}
              </span>
            </div>
          </div>
        )}

        {/* Member list */}
        <div className="space-y-2">
          {members.map((member, i) => (
            <motion.div
              key={member.name}
              className={`flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-100 p-3 ${isArabic ? "flex-row-reverse" : ""}`}
              initial={{ opacity: 0, x: isArabic ? 12 : -12 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: i * 0.08, duration: 0.4 }}
            >
              {/* Avatar */}
              <div className={`w-8 h-8 rounded-full ${avatarColors[i % avatarColors.length]} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                {getInitials(member.name)}
              </div>

              {/* Info */}
              <div className={`flex-1 min-w-0 ${isArabic ? "text-right" : ""}`}>
                <p className="text-xs font-bold text-slate-800 mb-0 truncate">{member.name}</p>
                <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full mt-0.5 ${roleColor(member.role)}`}>
                  {member.role}
                </span>
              </div>

              {/* Tasks count */}
              <div className="text-center flex-shrink-0">
                <p className="text-sm font-bold text-slate-700 mb-0">{member.tasksCompleted}</p>
                <p className="text-[8px] text-slate-400 mb-0">{isArabic ? "مهمة" : "tasks"}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Extra members note */}
        {totalMembers > members.length && (
          <p className={`text-[10px] text-slate-400 italic mb-0 ${isArabic ? "text-right" : ""}`}>
            +{totalMembers - members.length} {isArabic ? "أعضاء آخرين" : "more team members"}
          </p>
        )}
      </div>
    </div>
  );
};

export default TeamWidget;
